import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import MobileHeader from '../../components/feature/MobileHeader';
import { apiService } from '../../services/api';
import { Ticket, AlertTriangle, Info } from 'lucide-react';

interface Notification {
  id: string;
  user_id: number;
  type: 'ticket' | 'alert' | 'system';
  status: 'pendente' | 'processando' | 'concluido' | 'cancelado';
  title: string;
  message: string;
  ticket_number?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
}

const notificationIcons = {
  'ticket': Ticket,
  'alert': AlertTriangle,
  'system': Info
};

const notificationColors = {
  'ticket': 'from-blue-500 to-blue-600',
  'alert': 'from-orange-500 to-orange-600',
  'system': 'from-purple-500 to-purple-600'
};

export default function Notificacoes() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'ticket' | 'alert' | 'system'>('all');
  const [filterRead, setFilterRead] = useState<'all' | 'read' | 'unread'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
  }, [filterType, filterRead]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNotifications({
        type: filterType !== 'all' ? filterType : undefined,
        is_read: filterRead !== 'all' ? (filterRead === 'read') : undefined,
        limit: 50
      });
      
      let filtered = response?.data || [];
      
      // Sort by date (newest first)
      filtered.sort((a: Notification, b: Notification) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setNotifications(filtered);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await apiService.markNotificationAsReadPatch(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Agora mesmo';
    if (hours < 24) return `${hours}h atrás`;
    if (days < 7) return `${days}d atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Sidebar />
      <MobileHeader />
      
      <div className="md:ml-72 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-bold mb-2">Notificações</h1>
            <p className="text-white/60 text-sm">
              {unreadCount > 0 ? `${unreadCount} notificações não lidas` : 'Todas as notificações foram lidas'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Marcar Todas como Lidas
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex flex-wrap gap-6">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Tipo</label>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'Todos' },
                  { value: 'ticket', label: 'Tickets' },
                  { value: 'alert', label: 'Alertas' },
                  { value: 'system', label: 'Sistema' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setFilterType(value as any)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      filterType === value 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' 
                        : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Read Status Filter */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Status</label>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'Todos' },
                  { value: 'unread', label: 'Não Lidos' },
                  { value: 'read', label: 'Lidos' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setFilterRead(value as any)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      filterRead === value 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' 
                        : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-white/60">Carregando notificações...</p>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="w-8 h-8 text-white/40" />
            </div>
            <h3 className="text-xl font-bold mb-2">Nenhuma notificação</h3>
            <p className="text-white/60">
              {filterRead === 'unread' 
                ? 'Você não tem notificações não lidas' 
                : filterType !== 'all' 
                ? `Você não tem notificações do tipo ${filterType}`
                : 'Você ainda não recebeu nenhuma notificação'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => {
              const IconComponent = notificationIcons[notification.type];
              const gradientColors = notificationColors[notification.type];
              
              return (
                <div
                  key={notification.id}
                  className={`bg-white/5 backdrop-blur-sm border rounded-2xl p-6 hover:bg-white/10 transition-all cursor-pointer ${
                    notification.is_read 
                      ? 'border-white/10' 
                      : 'border-blue-500/30 bg-blue-500/5'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${gradientColors} rounded-xl flex-shrink-0`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-lg">{notification.title}</h3>
                        {!notification.is_read && (
                          <span className="w-3 h-3 bg-blue-400 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      
                      <p className="text-white/80 mb-3">{notification.message}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-white/60">
                            {formatDate(notification.created_at)}
                          </span>
                          {notification.ticket_number && (
                            <span className="text-sm text-white/40">
                              {notification.ticket_number}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            notification.status === 'pendente' 
                              ? 'bg-orange-500/20 text-orange-400'
                              : notification.status === 'concluido'
                              ? 'bg-green-500/20 text-green-400'
                              : notification.status === 'processando'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {notification.status}
                          </span>
                        </div>
                        
                        {!notification.is_read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification.id);
                            }}
                            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Marcar como lida
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
